/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { LogLevel } from "@grafana/faro-web-sdk";
import faroLogger from "../utils/faro";
import { FaroFeatures } from "../constant";

export enum FHIRCastEvent {
  // Home
  HOME_OPEN = "Home-open",

  // Patient
  PATIENT_OPEN = "Patient-open",
  PATIENT_CLOSE = "Patient-close",

  // Encounter
  ENCOUNTER_OPEN = "Encounter-open",
  ENCOUNTER_CLOSE = "Encounter-close",

  // ImagingStudy
  IMAGING_STUDY_OPEN = "ImagingStudy-open",
  IMAGING_STUDY_CLOSE = "ImagingStudy-close",

  // DiagnosticReport
  DIAGNOSTIC_REPORT_OPEN = "DiagnosticReport-open",
  DIAGNOSTIC_REPORT_CLOSE = "DiagnosticReport-close",
  DIAGNOSTIC_REPORT_UPDATE = "DiagnosticReport-update",
  DIAGNOSTIC_REPORT_SELECT = "DiagnosticReport-select",
}

export interface WssFHIRCastEventPayload {
  [FHIRCastEvent.HOME_OPEN]: { userId: string };

  [FHIRCastEvent.PATIENT_OPEN]: { patientId: string };
  [FHIRCastEvent.PATIENT_CLOSE]: { patientId: string };

  [FHIRCastEvent.ENCOUNTER_OPEN]: { encounterId: string };
  [FHIRCastEvent.ENCOUNTER_CLOSE]: { encounterId: string };

  [FHIRCastEvent.IMAGING_STUDY_OPEN]: { studyId: string };
  [FHIRCastEvent.IMAGING_STUDY_CLOSE]: { studyId: string };

  [FHIRCastEvent.DIAGNOSTIC_REPORT_OPEN]: { id?: string; reportId: string };
  [FHIRCastEvent.DIAGNOSTIC_REPORT_CLOSE]: { id?: string; reportId: string };
  [FHIRCastEvent.DIAGNOSTIC_REPORT_UPDATE]: {
    id?: string;
    reportId: string;
    status: string;
  };
  [FHIRCastEvent.DIAGNOSTIC_REPORT_SELECT]: { reportId: string };
}

const HEARTBEAT_TIMEOUT = 30000;

export interface WebSocketContextType {
  send: (raw: string | object) => void;

  sendEvent: <E extends FHIRCastEvent>(
    event: E,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: WssFHIRCastEventPayload[E] | any
  ) => void;

  subscribe: <E extends FHIRCastEvent>(
    event: E,
    callback: (data: WssFHIRCastEventPayload[E]) => void
  ) => () => void;

  isConnected: boolean;
  reconnectAttempt: number;
  retryNextDelay: number;
}

interface WebSocketProviderProps {
  token: string | null;
  children: React.ReactNode;
  url?: string;
  enabledHeartBeat?: boolean;
  allowWss?: boolean;
  resubscribe: () => void;
  setCurrentWSS: (wss: WebSocket | null) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const WebSocketProvider = ({
  children,
  token,
  url,
  enabledHeartBeat = false,
  allowWss = false,
  setCurrentWSS,
  resubscribe,
}: WebSocketProviderProps) => {
  const lastConnectKeyRef = useRef<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimer = useRef<NodeJS.Timeout | null>(null);
  const queue = useRef<string[]>([]);
  const retryAttempts = useRef(0);
  const [, setError] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [retryNextDelay, setRetryNextDelay] = useState(0);

  const logger = (message: string, topicId: string, level = LogLevel.INFO) => {
    switch (level) {
      case LogLevel.INFO:
        faroLogger.info(message, {
          feature: FaroFeatures.FHIRCAST,
          topic: topicId,
        });
        break;
      case LogLevel.WARN:
        const msgWar = message;
        faroLogger.warn(msgWar, {
          feature: FaroFeatures.FHIRCAST,
          topic: topicId,
        });
        break;
      default:
        break;
    }
  };

  //  NEW: event subscribers (like FHIRCast)
  const subscribers = useRef<
    Map<string, Set<(data: WssFHIRCastEventPayload) => void>>
  >(new Map());

  const buildConnectKey = (wssUrl: string, wssToken: string) =>
    `${wssUrl}::${wssToken}`;

  // HEARTBEAT
  const stopHeartbeat = () => {
    if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
  };

  const startHeartbeat = () => {
    stopHeartbeat();

    heartbeatTimer.current = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "PING" }));
      }
    }, HEARTBEAT_TIMEOUT);
  };

  // SUBSCRIBER Events
  const subscribe = useCallback(
    (eventName: string, callback: (data: any) => void) => {
      if (!subscribers.current?.has(eventName)) {
        subscribers.current.set(eventName, new Set());
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const set = subscribers.current.get(eventName)!;
      set.add(callback);
      return () => {
        set.delete(callback);
      };
    },
    []
  );

  //  Trigger event
  const emitToSubscribers = (eventName: string, data: any) => {
    const subs = subscribers.current.get(eventName);
    if (!subs) return;
    subs.forEach((cb) => cb(data));
  };

  // CONNECT
  const connect = (wssUrl: string, wssToken: string) => {
    const socket = new WebSocket(`${wssUrl}?token=${wssToken}`);
    socketRef.current = socket;
    setCurrentWSS(socket);
    logger(
      "WS CONNECTED",
      sessionStorage.getItem("fhircast-topic-id") as string
    );
    socket.onopen = () => {
      setIsConnected(true);
      retryAttempts.current = 0;
      setReconnectAttempt(0);
      setRetryNextDelay(0);
      while (queue.current.length > 0 && socket.readyState === WebSocket.OPEN) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        socket.send(queue.current.shift()!);
      }

      if (enabledHeartBeat) {
        startHeartbeat();
      }
    };

    const scheduleReconnect = () => {
      if (reconnectTimer.current) return;

      const retryDelay = Math.min(
        1000 * 3 ** retryAttempts.current, // first is 3s.
        HEARTBEAT_TIMEOUT
      );

      setReconnectAttempt(retryAttempts.current);
      setRetryNextDelay(retryDelay);

      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        retryAttempts.current += 1;
        setReconnectAttempt(retryAttempts.current);
        logger(
          "Retrying WS... attempt " + retryAttempts.current,
          sessionStorage.getItem("fhircast-topic-id") as string
        );
        resubscribe();
      }, retryDelay);
    };

    socket.onclose = (event) => {
      // eslint-disable-next-line no-console
      logger(
        `WS closed - Code: ${event.code} - Reason: ${event.reason}`,
        sessionStorage.getItem("fhircast-topic-id") as string
      );
      socketRef.current = null;
      lastConnectKeyRef.current = null;
      if (enabledHeartBeat) {
        stopHeartbeat();
      }
      // reset states.
      // 1008 = policy violation (auth)
      // 4401 / 4001 (custom auth close code)
      if ([1008, 4401, 4001].includes(event.code)) {
        setError({}); // trigger init wss.
        setIsConnected(false);
        subscribers.current.clear(); // clear subscriber.
        return;
      }

      if (event.code === 1000) {
        setIsConnected(false);
        subscribers.current.clear();
        return;
      }

      if (event.code === 1006) {
        setError({});
        scheduleReconnect();
        return;
      }
    };

    socket.onerror = (err) => {
      // reset state.
      setIsConnected(false);
      socketRef.current = null;
      lastConnectKeyRef.current = null;
      subscribers.current.clear(); // clear subscriber.

      logger(
        "WS closed by Error: " + JSON.stringify(err),
        sessionStorage.getItem("fhircast-topic-id") as string
      );
      scheduleReconnect();
    };

    //  Incoming message parser with event routing
    socket.onmessage = (msg) => {
      try {
        const packet = JSON.parse(msg.data);
        // init subscribers
        if (packet["hub.events"] && packet["hub.mode"] === "subscribe") {
          //
          const eventNames: string[] = packet["hub.events"]
            .split(", ")
            .filter((x: string) => x.trim());
          logger(
            `${packet["hub.events"]} events initialized.`,
            sessionStorage.getItem("fhircast-topic-id") as string
          );

          // fhircast-style message
          eventNames.forEach((evt) => {
            emitToSubscribers(evt, null);
          });
          return;
        }
        // dispatching specific events.
        // For DiagnosticReport-open, DiagnosticReport-update, DiagnosticReport-close, ....
        if (packet?.event && packet?.event["hub.event"]) {
          const eventName = packet?.event["hub.event"];
          if (!packet) {
            logger(
              `${eventName}: packet is undefined.`,
              sessionStorage.getItem("fhircast-topic-id") as string
            );
          }
          emitToSubscribers(eventName, packet);
        }
      } catch (e) {
        logger(
          "Something wrong when trying to get packet.",
          sessionStorage.getItem("fhircast-topic-id") as string
        );
      }
    };
  };

  // SEND
  const send = (data: any) => {
    const msg = typeof data === "string" ? data : JSON.stringify(data);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(msg);
    } else {
      queue.current.push(msg);
    }
  };

  // NEW: sendEvent("EVENT_NAME", payload)
  const sendEvent = (eventName: string, data: any) => {
    send({ event: eventName, data });
  };

  // INIT
  useEffect(() => {
    if (!allowWss || !token || !url) return;
    const connectKey = buildConnectKey(url, token);
    // dont allow if last connect URL is equal current connect URL.
    if (lastConnectKeyRef.current === connectKey) return;

    connect(url, token);
    lastConnectKeyRef.current = connectKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token, allowWss]);

  return (
    <WebSocketContext.Provider
      value={{
        send,
        sendEvent,
        subscribe,
        isConnected,
        reconnectAttempt,
        retryNextDelay,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWSS = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error("useWSS must be used inside WebSocketProvider");
  return ctx;
};
